import { graphql } from "gatsby";
import Slider from "react-slick";
import { useMediaQuery } from "react-responsive";
import { GatsbyImage } from "gatsby-plugin-image";
import { StaticImage } from "gatsby-plugin-image";
import React, { useCallback, useState } from "react";
import { ICalendar, GoogleCalendar } from "datebook";

import ArrowSVG from "../assets/arrow.svg";
import PrevArrowSVG from "../assets/PrevArrow.svg";
import Rectangle from "../assets/rectangles.svg";
import Plus from "../assets/plus.svg";
import Dot from "../assets/dot.svg";
import Circle from "../assets/circular.svg";
import Waves from "../assets/waves.svg";
import Check from "../assets/check.svg";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./styles.scss";
import "./ecommercecalendar.scss";
import { Helmet } from "react-helmet";

const EventCard = ({
  image,
  title: eventTitle,
  index,
  location: eventLoc,
  description: eventDesc,
  startTime: eventStart,
  endTime: eventEnd,
}) => {
  const description = eventDesc || "";
  const location = eventLoc || "";
  const title = eventTitle || "";

  const config = {
    title,
    location,
    description,
    start: new Date(eventStart),
    end: new Date(eventEnd),
  };

  const iCalendar = new ICalendar(config);
  const gCalendar = new GoogleCalendar(config);

  return (
    <div className={`event-card-${index + 1} event-card`}>
      <div className="event-card-display">
        <div className="event-img">
          <a
            href={gCalendar.render()}
            target="_blank"
            rel="noopener noreferrer"
          >
            Add
          </a>
          <a onClick={() => iCalendar.download()}>Download</a>
          <GatsbyImage
            image={image.asset.gatsbyImageData}
            alt={title}
            className="event-img-bg"
          />
        </div>
        <h3 className="event-title">{title}</h3>
      </div>
    </div>
  );
};

const Subscribe = ({ subHeading, heading, cta, text, image }) => {
  const isMobile = useMediaQuery({ query: "(max-width: 600px)" });
  return (
    <section className="subscribe my-10">
      <div className="subscribe-photo">
        <div className="subscribe-heading cap">{heading}</div>
        <div className="image-container">
          <GatsbyImage
            className="image"
            image={image.asset.gatsbyImageData}
            alt="subscribe-image"
          />
          <div className="image-text">{text}</div>
        </div>
        <div className="subscribe-footer">{subHeading}</div>
      </div>
      <div className="subscribe-form">
        <iframe
          src="https://theappcollective.substack.com/embed"
          width={isMobile ? "364" : "480"}
          height={isMobile ? "300" : "320"}
          style={{ background: "#ffdb96" }}
          frameborder="0"
          scrolling="no"
        ></iframe>
      </div>
    </section>
  );
};

const Ecommercecalendar = ({ data }) => {
  const {
    hero,
    calendar: months,
    poweredBy,
    quotesSection: quotes,
    subscribeSection,
    whyJoin,
    whoWeHeading,
    tribeHeading,
    whyJoinHeading,
    testimonialsSection,
  } = data.allSanityEcommerceCalendar.nodes[0];

  React.useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      html,body {
        height: 100%;
        scroll-behavior: smooth;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [index, setIndex] = useState(0);

  // header controller
  const [headerOpen, setHeaderOpen] = useState(false);
  const isDesktop = useMediaQuery({
    query: "(min-width: 768px)",
  });
  const isBigBreakpoint = useMediaQuery({ query: "(max-width: 1200px)" });
  const isSmallBreakpoint = useMediaQuery({ query: "(max-width: 500px)" });

  const settingslogo = useCallback(
    (type = "review") => {
      const isReview = type === "review";
      return {
        dots: false,
        infinite: true,
        speed: 500,
        className: isReview ? "review-slider" : "testimonial-slider",
        slidesToShow: isReview ? 3 : 1,
        slidesToScroll: 1,
        touchMove: true,
        prevArrow: <PrevArrowSVG />,
        nextArrow: <PrevArrowSVG />,
        responsive: [
          {
            breakpoint: 500,
            settings: {
              slidesToShow: 1,
            },
          },
          {
            breakpoint: 1400,
            settings: {
              slidesToShow: isReview ? 2 : 1,
            },
          },
        ],
      };
    },
    [isSmallBreakpoint, isBigBreakpoint]
  );

  const meta = {
    title: "The App Collective",
    url: "https://theappcollective.us",
    image:
      "https://media.kubric.io/api/assetlib/58f33573-d175-4cd2-9f93-9512a11ca09d.png",
    description:
      "🚀 The fastest way to make a 6-figure eCommerce business. Sign up for The App Collective's eCommerce Hub today for Live Events, Expert Guides & Exclusive App Offers from top eCommerce solutions.",
  };

  return (
    <div className="dark-background">
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={meta.image} />
        <meta property="og:url" content={meta.url} />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />
        <meta name="twitter:card" content={meta.title} />
      </Helmet>
      <main
        className={`flex w-full h-full flex-col ${
          headerOpen ? "header-open" : "header-close"
        }`}
      >
        <header className="header">
          <div className="logo-X-container">
            <div className="lp-logo">
              <StaticImage
                src="../assets/logo.svg"
                alt="We are The App Collective"
                className="real-logo"
              />
            </div>
            <div
              className={`ham-menu flex flex-col items-center md:hidden ${
                headerOpen ? "ham-active" : ""
              }`}
              onClick={() => setHeaderOpen((open) => !open)}
            >
              <div className="ham-line"></div>
              <div className="ham-line"></div>
              <div className="ham-line"></div>
            </div>
          </div>
          <navbar>
            <a
              className="lp-link"
              onClick={() => setHeaderOpen((open) => !open)}
              href="#tribe"
            >
              Our Tribe
            </a>
            <a
              className="lp-link"
              onClick={() => setHeaderOpen((open) => !open)}
              href="#updates"
            >
              Updates
            </a>
            <a
              className="lp-link"
              onClick={() => setHeaderOpen((open) => !open)}
              href="#why"
            >
              Why Us?
            </a>
            <a
              className="lp-link lp-button"
              onClick={() => setHeaderOpen((open) => !open)}
              href="#join"
            >
              Join
            </a>
          </navbar>
        </header>
        <div className="ecal-hero">
          <h4 className="hero-pre-heading text-md md:text-xl font-light">
            {hero.topPreHeading} {isDesktop && <br />} {hero.bottomPreHeading}
          </h4>
          <br />
          <h1 className="hero-heading text-6xl md:text-7xl font-medium relative">
            {hero.beforeArrowHeading}{" "}
            <span className="absolute ultimate">{hero.specialWord}</span>{" "}
            <span className="carret">
              <ArrowSVG className="arrow-svg" />
            </span>{" "}
            {hero.afterArrowHeading}
          </h1>
          <p className="sub-heading md:text-3xl mt-8">{hero.description}</p>
          <div className="z-10 pt-8 cta-container">
            <a href="#updates" className="cta-button">
              {hero.cta}
            </a>
          </div>
          <div className="hero-display">
            <div className="image-wrapper">
              <GatsbyImage
                className="hero-image"
                image={hero.image.asset.gatsbyImageData}
                imgStyle={{ objectFit: "contain" }}
                alt="hero image"
              />
            </div>
          </div>
        </div>
        <div className="app-collective">
          <div className="hero-design-elements">
            <Circle className="hero-design-circle" />
            <div className="hero-filled-circle">
              <Circle className="hero-circle" />
            </div>
            <Waves className="hero-waves" />
          </div>
          <h3 className="app-collective__heading">{whyJoinHeading}</h3>
          <div className="app-collective__checkmarks">
            {whyJoin.map((check) => (
              <div className="app-collective__checkmarkbox" key="check">
                <Check className="app-collective__check" />
                <p className="app-collective__text">{check}</p>
              </div>
            ))}
          </div>
        </div>
        <div
          id="updates"
          className="relative cal-wrap items-baseline md:grid grid-cols-4 gap-8 pt-8"
        >
          <div className="cal-list sticky top-0 months px-4 col-span-1">
            {months.map((card, i) => (
              <a href={`#${card.month}`}>
                <h3
                  className={`my-3 font-medium ${
                    index === i ? "mon-active" : "mon-inactive"
                  }`}
                  onClick={() => {
                    card.events.length !== 0 ? setIndex(i) : setIndex(index);
                  }}
                >
                  <span className="emoji">
                    {index === i ? "👉" : card.emoji}
                  </span>{" "}
                  <span>{card.month}</span>
                </h3>
              </a>
            ))}
          </div>
          <div className="events col-span-3 divide">
            {months.map((card, i) => (
              <div
                id={card.month}
                className={`${
                  !card.events.length
                    ? "coming-soon-event-wrap"
                    : "event-cards-wrap"
                } relative`}
              >
                {i === 1 && isDesktop && (
                  <>
                    <Circle className="months-first-ring-above" />
                    <Dot className="months-first-dot-below" />
                  </>
                )}
                {i === 2 && isDesktop && (
                  <>
                    <Dot className="months-dot-above" />
                    <Dot className="months-dot-below" />
                  </>
                )}
                {i === 3 && isDesktop && (
                  <Rectangle className="months-rectangle-above" />
                )}
                <h1 className="event-month-heading">
                  <span className="emoji">{card.emoji}</span> {card.month} :{" "}
                  {card.tagline}
                </h1>
                {card.events.length ? (
                  <div className="events-wrapper">
                    {card.events.map((item, index) => (
                      <EventCard {...item} index={index} />
                    ))}
                  </div>
                ) : (
                  <div className="coming-soon mt-4 p-4 mr-10">
                    <p className="text-2xl text-opacity-30">Coming Soon...</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <section id="tribe" className="companies review-companies-container">
          {isDesktop && <Plus className="slider-plus-above" />}
          <h2 className="slider-heading">{tribeHeading}</h2>
          <div className="sliders-container">
            <Slider {...settingslogo()}>
              {quotes.map((c, i) => (
                <div className="review-container" key={c.name}>
                  <div
                    className={`review ${i % 2 ? "odd-review" : "even-review"}`}
                  >
                    <div className="reviewer">
                      <GatsbyImage
                        image={c.image.asset.gatsbyImageData}
                        className="review-logo"
                        alt={c.name}
                      />
                      <div className="reviewer-details">
                        <p className="reviewer-name">{c.name}</p>
                        <p className="reviewer-desig">{c.title}</p>
                      </div>
                    </div>
                    <p className="review-text">{c.quote}</p>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </section>
        <section id="why" className="companies testimonial-companies-container">
          {isDesktop && <Plus className="slider-plus-above" />}
          <h2 className="slider-heading">{whoWeHeading}</h2>
          <div className="sliders-container">
            <Slider {...settingslogo("testimonial")}>
              {testimonialsSection.map((c, i) => (
                <div className="review-container" key={c.name}>
                  <div
                    className={`review testimonial ${
                      i % 2 ? "odd-review" : "even-review"
                    }`}
                  >
                    <div className="mul-logos-container">
                      <GatsbyImage
                        image={c.image.asset.gatsbyImageData}
                        className="review-logo"
                        alt={c.name}
                      />
                    </div>
                    <p className="review-text">{c.quote}</p>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </section>
        <div
          id="join"
          className="w-full flex items-center relative subsribe-container"
        >
          <Subscribe {...subscribeSection} />
          {isDesktop && (
            <>
              <Rectangle className="subscribe-rectangle-below" />
              <StaticImage
                className="subscribe-lines"
                src="../assets/lp-subscribe-lines.png"
                alt="design line"
              />
            </>
          )}
        </div>
      </main>
      <footer>
        <StaticImage
          className="logo"
          src="../assets/newlogo.png"
          alt="design lines"
        />
        <div className="poweredby">{poweredBy}</div>
      </footer>
    </div>
  );
};

export const query = graphql`
  query {
    allSanityEcommerceCalendar {
      nodes {
        hero {
          afterArrowHeading
          beforeArrowHeading
          cta
          image {
            asset {
              gatsbyImageData(fit: CLIP, placeholder: BLURRED)
            }
          }
          description
          topPreHeading
          bottomPreHeading
          specialWord
        }
        quotesSection {
          name
          quote
          title
          image {
            asset {
              gatsbyImageData(fit: FILLMAX, placeholder: BLURRED)
            }
          }
        }
        subscribeSection {
          subHeading
          heading
          cta
          text
          image {
            asset {
              gatsbyImageData(fit: CLIP, placeholder: BLURRED)
            }
          }
        }
        poweredCTA
        poweredBy
        calendar {
          emoji
          month
          tagline
          events {
            title
            location
            description
            startTime
            endTime
            image {
              asset {
                gatsbyImageData(fit: FILLMAX, placeholder: BLURRED)
              }
            }
          }
        }
        whyJoin
        whoWeHeading
        tribeHeading
        whyJoinHeading
        testimonialsSection {
          name
          quote
          image {
            asset {
              gatsbyImageData
            }
          }
        }
      }
    }
  }
`;

export default Ecommercecalendar;
